import type { PostedWithin, Seniority } from "../catalogue";
import { getJson, postEmpty, postJson, putJson } from "../client";
import { ENVOY_BASE } from "./base";

/** One thing envoy refused in a save. `envoy_api::refusal::RefusedSetting`. */
export interface RefusedSetting {
    /** `kind` says which fault, and the fields beside it what about it. */
    fault: { kind: string } & Record<string, unknown>;
    /** The sentence to show. */
    message: string;
}

/** How far the envoy goes on its own. It never sends: the person does. */
export type StandingOrder = "FIND" | "TAILOR";

/** Why a person put their envoy to rest. Only `TOO_MANY_MATCHES` goes on finding. */
export type PauseReason = "GOT_A_JOB" | "BREAK" | "TOO_MANY_MATCHES";

export const PAUSE_REASONS: readonly PauseReason[] = ["GOT_A_JOB", "BREAK", "TOO_MANY_MATCHES"];

export type EnvoyWorkSetting = "REMOTE" | "HYBRID" | "ON_SITE";
export type Currency = "USD" | "CAD" | "EUR" | "GBP";
export const CURRENCIES: readonly Currency[] = ["USD", "CAD", "EUR", "GBP"];

/** The least a posting may pay, as base pay for a year. */
export interface PayFloor {
    currency: Currency;
    yearly: number;
    /** Whether a posting that states no pay still matches. */
    keep_unstated: boolean;
}

/** What a person tells their envoy. `envoy_api::settings::EnvoySettings`. */
export interface EnvoySettings {
    /** The gateway's model id. Absent, the cheapest the plan includes. */
    model?: string | null;
    titles: string[];
    skip_titles: string[];
    must_mention: string[];
    pay?: PayFloor | null;
    work_settings: EnvoyWorkSetting[];
    /**
     * ISO 3166-1 alpha-2. Empty means anywhere. Hybrid and on-site work is
     * looked for near the envoy's area (`./area`) instead.
     */
    countries: string[];
    seniority: Seniority[];
    /**
     * ISO 639-1 codes the posting must be written in. Empty means any
     * language; absent, English.
     */
    languages?: string[];
    order: StandingOrder;
    /** 255 characters at most. */
    instructions?: string | null;
    posted_within?: PostedWithin | null;
}

export interface EnvoyPause {
    reason: PauseReason;
    at: string;
}

export interface EnvoyHold {
    at: string;
}

/** A person's envoy. `envoy_api::responses::Envoy`. */
export interface Envoy {
    settings: EnvoySettings;
    settings_version: number;
    /** Present exactly while the envoy rests. */
    paused: EnvoyPause | null;
    /** Present exactly while its tailoring is held. */
    held?: EnvoyHold | null;
    created_at: string;
    updated_at: string;
}

/** The caller's envoy. 404 until its settings have been saved once. */
export function getEnvoy(): Promise<Envoy> {
    return getJson(`${ENVOY_BASE}/settings`);
}

/** Saves the settings whole. The first save starts the envoy; a refusal names every fault. */
export function saveEnvoySettings(settings: EnvoySettings): Promise<Envoy> {
    return putJson(`${ENVOY_BASE}/settings`, settings);
}

export function pauseEnvoy(reason: PauseReason): Promise<Envoy> {
    return postJson(`${ENVOY_BASE}/pause`, { reason });
}

export function unpauseEnvoy(): Promise<Envoy> {
    return postEmpty(`${ENVOY_BASE}/unpause`);
}
